import httpClient from '../config/httpClient';

/**
 * Get all invoices for a company
 * @param {string} companyId - Company ID
 * @param {number} page - Page number (default: 1)
 * @param {number} limit - Number of invoices per page (default: 20)
 * @returns {Promise} API response with invoices data
 */
async function getCompanyInvoices(companyId, page = 1, limit = 20) {
  try {
    const response = await httpClient.get('/getCompanyInvoices', {
      params: {
        companyId,
        page,
        limit,
      },
      timeout: 30000,
    });

    if (response.data.success) {
      return {
        success: true,
        data: response.data.invoices,
        pagination: response.data.pagination,
        message: response.data.message,
      };
    } else {
      throw new Error(response.data.message || 'Failed to fetch invoices');
    }
  } catch (error) {
    console.error('Error fetching company invoices:', error);
    return {
      success: false,
      data: [],
      pagination: {
        page: 1,
        limit: 20,
        total: 0,
        totalPages: 0,
      },
      error:
        error.response?.data?.message ||
        error.message ||
        'Failed to fetch invoices',
    };
  }
}

/**
 * Get a single invoice by ID
 * @param {string} companyId - Company ID
 * @param {string} invoiceId - Invoice ID
 * @returns {Promise} API response with invoice data
 */
async function getInvoice(companyId, invoiceId) {
  try {
    console.log('Fetching invoice with params:', { companyId, invoiceId });

    const response = await httpClient.get('/getInvoice', {
      params: {
        companyId,
        invoiceId,
      },
      timeout: 30000,
    });

    console.log('Invoice API response:', response.data);

    if (response.data.success) {
      console.log('Invoice data received:', response.data.invoice);
      return {
        success: true,
        data: response.data.invoice,
        message: response.data.message,
      };
    } else {
      console.error('Invoice API returned error:', response.data);
      throw new Error(response.data.message || 'Failed to fetch invoice');
    }
  } catch (error) {
    console.error('Error fetching invoice:', error);
    console.error('Error response:', error.response?.data);
    return {
      success: false,
      data: null,
      error:
        error.response?.data?.message ||
        error.message ||
        'Failed to fetch invoice',
    };
  }
}

/**
 * Open Stripe invoice in a new tab
 * @param {string} stripeInvoiceUrl - Stripe invoice URL
 * @returns {boolean} Success status
 */
function openStripeInvoice(stripeInvoiceUrl) {
  try {
    if (!stripeInvoiceUrl) {
      console.error('No invoice URL provided');
      return false;
    }

    // Validate URL is from Stripe
    if (!stripeInvoiceUrl.includes('invoice.stripe.com')) {
      console.error('Invalid invoice URL - not from Stripe');
      return false;
    }

    // Open in new tab
    window.open(stripeInvoiceUrl, '_blank', 'noopener,noreferrer');
    return true;
  } catch (error) {
    console.error('Error opening Stripe invoice:', error);
    return false;
  }
}

/**
 * Format currency amount for display
 * @param {number} amount - Amount in cents
 * @param {string} currency - Currency code (default: 'usd')
 * @returns {string} Formatted currency string
 */
function formatCurrency(amount, currency = 'usd') {
  try {
    const value = amount / 100; // Convert from cents to dollars
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(value);
  } catch (error) {
    console.error('Error formatting currency:', error);
    return `$${(amount / 100).toFixed(2)}`;
  }
}

/**
 * Format date for display
 * @param {string|Date} date - Date to format
 * @returns {string} Formatted date string
 */
function formatDate(date) {
  try {
    if (!date) return 'N/A';
    let dateObj = date;
    if (typeof date === 'string') {
      dateObj = new Date(date);
    } else if (typeof date === 'number') {
      dateObj = new Date(date);
    }
    return dateObj.toDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'Invalid Date';
  }
}

/**
 * Format billing period for display
 * @param {Object} billingPeriod - Billing period object with start and end dates
 * @returns {string} Formatted billing period string
 */
function formatBillingPeriod(billingPeriod) {
  try {
    if (!billingPeriod || !billingPeriod.start || !billingPeriod.end) {
      return 'N/A';
    }

    const startDate = formatDate(billingPeriod.start?.seconds * 1000);
    const endDate = formatDate(billingPeriod.end?.seconds * 1000);

    return `${startDate} - ${endDate}`;
  } catch (error) {
    console.error('Error formatting billing period:', error);
    return 'N/A';
  }
}

/**
 * Get status badge color for invoice status
 * @param {string} status - Invoice status
 * @returns {string} CSS class name for status badge
 */
function getStatusBadgeColor(status) {
  const statusColors = {
    paid: 'success',
    pending: 'warning',
    failed: 'danger',
    draft: 'secondary',
    open: 'info',
    void: 'secondary',
    uncollectible: 'dark',
  };

  return statusColors[status] || 'secondary';
}

/**
 * Get human-readable status text
 * @param {string} status - Invoice status
 * @returns {string} Human-readable status
 */
function getStatusText(status) {
  const statusTexts = {
    paid: 'Paid',
    pending: 'Pending',
    failed: 'Failed',
    draft: 'Draft',
    open: 'Open',
    void: 'Void',
    uncollectible: 'Uncollectible',
  };

  return (
    statusTexts[status] || status.charAt(0).toUpperCase() + status.slice(1)
  );
}

export default {
  getCompanyInvoices,
  getInvoice,
  openStripeInvoice,
  formatCurrency,
  formatDate,
  formatBillingPeriod,
  getStatusBadgeColor,
  getStatusText,
};
